import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

/** Response from GET /actuator/health */
export interface ActuatorHealthResponse {
    status: 'UP' | 'DOWN' | 'OUT_OF_SERVICE' | 'UNKNOWN';
    components?: {
        [key: string]: { status: string; [key: string]: unknown };
    };
}

/** Response from GET /internal/cpu */
export interface CpuStatsResponse {
    systemCpuLoad: number;
    processCpuLoad: number;
    systemCpuPercentage: string;
    processCpuPercentage: string;
    availableProcessors: number;
    health: {
        status: 'HEALTHY' | 'WARNING' | 'UNHEALTHY' | 'UNKNOWN';
        cpuUtilization: string;
        recommendation: string;
    };
}

/** Response from GET /internal/memory */
export interface MemoryStatsResponse {
    heap: {
        used: number;
        max: number;
        committed: number;
        usedMB: string;
        maxMB: string;
        utilizationPercentage: string;
    };
    nonHeap: { usedMB: string; maxMB: string; committedMB: string };
    total: { usedMB: string; maxMB: string };
    health: {
        status: string;
        heapUtilization: string;
        isHighMemoryUsage: boolean;
        isCriticalMemoryUsage: boolean;
        recommendation: string;
    };
    system?: { availableProcessors: number; maxSystemMemoryMB: string };
}

/** Response from GET /internal/hikari */
export interface HikariStatsResponse {
    activeConnections: number;
    idleConnections: number;
    totalConnections: number;
    threadsAwaitingConnection: number;
    maximumPoolSize: number;
    minimumIdle: number;
    poolName: string;
    poolHealth: 'HEALTHY' | 'UNHEALTHY';
    healthDetails: {
        hasAvailableConnections: boolean;
        isPoolExhausted: boolean;
        isPoolAtCapacity: boolean;
        utilizationPercentage: string;
    };
}

/** Response from GET /internal/uptime */
export interface UptimeResponse {
    uptimeMillis: number;
    uptimeSeconds: number;
    uptimeHuman: string;
    startTimeMillis: number;
    startTimeIso: string;
}

/** Response from GET /internal/errors/5xx */
export interface Error5xxResponse {
    windowMinutes: number;
    lastWindowCount: number;
    status: 'HEALTHY' | 'WARNING' | 'UNHEALTHY';
    recommendation: string;
}

@Injectable({ providedIn: 'root' })
export class MonitoringService {
    /** Order management backend (existing implementation). */
    private readonly base = environment.apiUrl.replace(/\/$/, '');

    constructor(private http: HttpClient) {}

    getHealth(): Observable<ActuatorHealthResponse> {
        return this.http.get<ActuatorHealthResponse>(`${this.base}/internal/health`);
    }

    getMemory(): Observable<MemoryStatsResponse> {
        return this.http.get<MemoryStatsResponse>(`${this.base}/internal/memory`);
    }

    getHikari(): Observable<HikariStatsResponse> {
        return this.http.get<HikariStatsResponse>(`${this.base}/internal/hikari`);
    }

    getCpu(): Observable<CpuStatsResponse> {
        return this.http.get<CpuStatsResponse>(`${this.base}/internal/cpu`);
    }

    getUptime(): Observable<UptimeResponse> {
        return this.http.get<UptimeResponse>(`${this.base}/internal/uptime`);
    }

    getError5xx(): Observable<Error5xxResponse> {
        return this.http.get<Error5xxResponse>(`${this.base}/internal/errors/5xx`);
    }
}

/**
 * User management monitoring service.
 * Uses authApiUrl as base and the same /internal endpoints to fetch metrics
 * for the authentication/UI backend.
 */
@Injectable({ providedIn: 'root' })
export class UserMonitoringService {
    private readonly base = environment.authApiUrl.replace(/\/$/, '');

    constructor(private http: HttpClient) {}

    getHikari(): Observable<HikariStatsResponse> {
        return this.http.get<HikariStatsResponse>(`${this.base}/internal/hikari`);
    }

    getHealth(): Observable<ActuatorHealthResponse> {
        return this.http.get<ActuatorHealthResponse>(`${this.base}/internal/health`);
    }

    getMemory(): Observable<MemoryStatsResponse> {
        return this.http.get<MemoryStatsResponse>(`${this.base}/internal/memory`);
    }

    getCpu(): Observable<CpuStatsResponse> {
        return this.http.get<CpuStatsResponse>(`${this.base}/internal/cpu`);
    }

    getUptime(): Observable<UptimeResponse> {
        return this.http.get<UptimeResponse>(`${this.base}/internal/uptime`);
    }

    getError5xx(): Observable<Error5xxResponse> {
        return this.http.get<Error5xxResponse>(`${this.base}/internal/errors/5xx`);
    }
}
